import React, { useMemo, useState } from "react";
import {
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    Typography,
    IconButton,
    Button,
    Chip,
    Avatar,
    CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LaptopMacIcon from "@mui/icons-material/LaptopMac";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import { routes } from "../constants/api-routes";

export interface RawSession {
    session_id: string;
    device_name: string;
    created_at: string;
}

interface ActiveLoginSessionsProps {
    open: boolean;
    onClose: () => void;
    sessions: RawSession[];
    accessToken: string;
    currentSessionId?: string;
    revokeUrl?: string;
    onSessionsChanged?: (remaining: RawSession[]) => void;
}

function parseUserAgent(ua: string) {
    const isMobile = /iphone|android.*mobile|mobile safari/i.test(ua) && !/ipad/i.test(ua);
    const isTablet = /ipad|tablet/i.test(ua);
    const deviceType = isMobile || isTablet ? "mobile" : "laptop";

    let os = "Unknown OS";
    if (/windows nt/i.test(ua)) os = "Windows";
    else if (/mac os x/i.test(ua)) os = "macOS";
    else if (/android/i.test(ua)) os = "Android";
    else if (/iphone|ipad|ios/i.test(ua)) os = "iOS";
    else if (/linux/i.test(ua)) os = "Linux";

    let browser = "Unknown Browser";
    let browserVersion = "";
    const edgeMatch = ua.match(/Edg\/([\d.]+)/);
    const chromeMatch = ua.match(/Chrome\/([\d.]+)/);
    const firefoxMatch = ua.match(/Firefox\/([\d.]+)/);
    const safariMatch = ua.match(/Version\/([\d.]+).*Safari/);

    if (edgeMatch) {
        browser = "Edge";
        browserVersion = edgeMatch[1];
    } else if (chromeMatch) {
        browser = "Chrome";
        browserVersion = chromeMatch[1];
    } else if (firefoxMatch) {
        browser = "Firefox";
        browserVersion = firefoxMatch[1];
    } else if (safariMatch) {
        browser = "Safari";
        browserVersion = safariMatch[1];
    }

    const device = `${browser} on ${os}`;
    const iconColorMap: Record<string, string> = {
        Chrome: "#34A853",
        Edge: "#9E9E9E",
        Firefox: "#F59E0B",
        Safari: "#9C27B0",
    };

    return {
        device,
        os,
        browser: browserVersion ? `${browser} ${browserVersion}` : browser,
        deviceType,
        iconColor: iconColorMap[browser] || "#4285F4",
    };
}

// function formatLastActive(createdAt: string) {
//     const created = new Date(createdAt.replace(" at", ""));
//     if (isNaN(created.getTime())) return createdAt;

//     const diffMs = Date.now() - created.getTime();
//     const diffMins = Math.floor(diffMs / 60000);

//     if (diffMins < 1) return "Just now";
//     if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? "s" : ""} ago`;
//     const diffHours = Math.floor(diffMins / 60);
//     if (diffHours < 24) return `${diffHours} hr${diffHours > 1 ? "s" : ""} ago`;
//     const diffDays = Math.floor(diffHours / 24);
//     if (diffDays === 1) return "Yesterday";
//     return `${diffDays} days ago`;
// }

function formatLastActive(createdAt: string) {
    const created = new Date(createdAt.replace(" at", "") + " UTC");
    if (isNaN(created.getTime())) return createdAt;
    return new Intl.DateTimeFormat("en-IN", {
        timeZone: "Asia/Kolkata",
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
    }).format(created);
}

function DeviceAvatar({ deviceType, iconColor }: any) {
    const Icon = deviceType === "mobile" ? SmartphoneIcon : LaptopMacIcon;
    return (
        <Avatar
            sx={{
                width: 52,
                height: 52,
                bgcolor: `${iconColor}18`,
                border: `1.5px solid ${iconColor}30`,
                flexShrink: 0,
            }}
        >
            <Icon sx={{ color: iconColor, fontSize: 26 }} />
        </Avatar>
    );
}

function SessionRow({ session, onLogout, loggingOutId }: any) {
    const { isCurrent } = session;
    const isLoggingOut = loggingOutId === session.id;
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                px: 2.5,
                py: 2,
                border: isCurrent ? "1.5px solid #34A85355" : "1px solid #e5e7eb",
                borderRadius: 2.5,
                bgcolor: isCurrent ? "#f0fdf4" : "#fff",
                transition: "box-shadow 0.2s ease",
                "&:hover": { boxShadow: "0 2px 12px rgba(0,0,0,0.07)" },
            }}
        >
            <DeviceAvatar deviceType={session.deviceType} iconColor={session.iconColor} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: "#111827", lineHeight: 1.3 }}>
                        {session.device}
                    </Typography>
                    {isCurrent && (
                        <Chip
                            label="Current Session"
                            size="small"
                            sx={{
                                height: 22,
                                fontSize: "0.7rem",
                                fontWeight: 600,
                                bgcolor: "#dcfce7",
                                color: "#15803d",
                                border: "1px solid #bbf7d0",
                                "& .MuiChip-label": { px: 1 },
                            }}
                        />
                    )}
                </Box>

                <Typography sx={{ color: "#6b7280", fontSize: "0.8rem", mt: 0.3 }}>
                    {session.os}
                    <Box component="span" sx={{ mx: 0.75, color: "#d1d5db" }}>•</Box>
                    {session.browser}
                </Typography>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 0.5, flexWrap: "wrap" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
                        <LanguageOutlinedIcon sx={{ fontSize: 13, color: "#9ca3af" }} />
                        <Typography sx={{ color: "#6b7280", fontSize: "0.78rem" }} title={session.id}>
                            Session {session.id.slice(0, 8)}
                        </Typography>
                    </Box>
                </Box>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", minWidth: 140, gap: 0.2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    {isCurrent ? (
                        <CheckCircleOutlineIcon sx={{ fontSize: 16, color: "#34A853" }} />
                    ) : (
                        <AccessTimeIcon sx={{ fontSize: 14, color: "#9ca3af" }} />
                    )}
                    <Typography sx={{ fontWeight: 600, fontSize: "0.82rem", color: isCurrent ? "#15803d" : "#374151" }}>
                        {session.lastActive}
                    </Typography>
                </Box>
                <Typography sx={{ color: "#9ca3af", fontSize: "0.72rem", pl: isCurrent ? 0 : 2.8 }}>
                    Last active
                </Typography>
            </Box>

            {!isCurrent && (
                <Button
                    variant="outlined"
                    size="small"
                    disabled={isLoggingOut}
                    startIcon={
                        isLoggingOut ? (
                            <CircularProgress size={14} sx={{ color: "#DC2626" }} />
                        ) : (
                            <LogoutIcon sx={{ fontSize: "14px !important" }} />
                        )
                    }
                    onClick={() => onLogout(session.id)}
                    sx={{
                        color: "#DC2626",
                        borderColor: "#DC2626",
                        fontSize: "0.78rem",
                        fontWeight: 600,
                        px: 1.5,
                        py: 0.7,
                        minWidth: 90,
                        borderRadius: 2,
                        textTransform: "none",
                        flexShrink: 0,
                        "&:hover": { borderColor: "#B91C1C", bgcolor: "#FEF2F2" },
                    }}
                >
                    {isLoggingOut ? "..." : "Logout"}
                </Button>
            )}
        </Box>
    );
}

const DEFAULT_REVOKE_URL = `${process.env.REACT_APP_UAT_URL}${routes?.authentication?.sessionLogOut}`;

export default function ActiveLoginSessions({
    open,
    onClose,
    sessions: rawSessions,
    accessToken,
    currentSessionId,
    revokeUrl = DEFAULT_REVOKE_URL,
    onSessionsChanged,
}: ActiveLoginSessionsProps) {
    const [removedIds, setRemovedIds] = useState<string[]>([]);
    const [loggingOutId, setLoggingOutId] = useState<string | null>(null);
    const [error, setError] = useState<string>("");

    const sessions = useMemo(() => {
        return rawSessions
            .filter((s) => !removedIds.includes(s.session_id))
            .map((s) => {
                const parsed = parseUserAgent(s.device_name);
                return {
                    id: s.session_id,
                    isCurrent: s.session_id === currentSessionId,
                    lastActive: formatLastActive(s.created_at),
                    ...parsed,
                };
            });
    }, [rawSessions, removedIds, currentSessionId]);

    const revokeSession = async (sessionId: string) => {
        const res = await fetch(revokeUrl, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ session_id: sessionId }),
        });
        if (!res.ok) {
            throw new Error(`Failed to revoke session (${res.status})`);
        }
    };

    const handleLogout = async (id: string) => {
        setError("");
        setLoggingOutId(id);
        try {
            await revokeSession(id);
            const next = [...removedIds, id];
            setRemovedIds(next);
            const remaining = rawSessions.filter((s) => !next.includes(s.session_id));
            onSessionsChanged?.(remaining);
        } catch (e: any) {
            setError(e?.message || "Could not log out that session. Please try again.");
        } finally {
            setLoggingOutId(null);
        }
    };

    const handleLogoutAll = async () => {
        setError("");
        const targets = sessions.filter((s) => !s.isCurrent).map((s) => s.id);
        for (const id of targets) {
            setLoggingOutId(id);
            try {
                await revokeSession(id);
            } catch (e: any) {
                setError(e?.message || "Some sessions could not be logged out.");
            }
        }
        const next = [...removedIds, ...targets];
        setRemovedIds(next);
        setLoggingOutId(null);
        const remaining = rawSessions.filter((s) => !next.includes(s.session_id));
        onSessionsChanged?.(remaining);
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
                    maxWidth: 560,
                    fontFamily: '"Inter", "Roboto", sans-serif',
                },
            }}
        >
            <DialogTitle sx={{ pb: 0.5, pt: 3, px: 3, display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.15rem", color: "#111827" }}>
                        Active Login Sessions
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#6b7280", fontSize: "0.82rem", mt: 0.5 }}>
                        You've reached the device limit. Log out of one or more sessions below to continue.
                    </Typography>
                </Box>
                <IconButton onClick={onClose} size="small" sx={{ color: "#6b7280", mt: -0.5, mr: -1, "&:hover": { bgcolor: "#f3f4f6" } }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ px: 3, pt: 2, pb: 3 }}>
                {error && (
                    <Typography sx={{ color: "#DC2626", fontSize: "0.8rem", mb: 1.5 }}>{error}</Typography>
                )}

                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    {sessions.length === 0 ? (
                        <Typography sx={{ color: "#6b7280", fontSize: "0.85rem", textAlign: "center", py: 2 }}>
                            No other active sessions.
                        </Typography>
                    ) : (
                        sessions.map((session) => (
                            <SessionRow key={session.id} session={session} onLogout={handleLogout} loggingOutId={loggingOutId} />
                        ))
                    )}
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mt: 2.5,
                        pt: 2,
                        borderTop: "1px solid #f3f4f6",
                        flexWrap: "wrap",
                        gap: 1.5,
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, maxWidth: 260 }}>
                        <SecurityOutlinedIcon sx={{ fontSize: 18, color: "#9ca3af", mt: 0.1, flexShrink: 0 }} />
                        <Typography sx={{ color: "#6b7280", fontSize: "0.78rem", lineHeight: 1.5 }}>
                            Once you're under the device limit, you can log in again.
                        </Typography>
                    </Box>

                    <Button
                        variant="outlined"
                        startIcon={<LogoutIcon sx={{ fontSize: "15px !important" }} />}
                        onClick={handleLogoutAll}
                        disabled={sessions.filter((s) => !s.isCurrent).length === 0 || loggingOutId !== null}
                        sx={{
                            color: "#DC2626",
                            borderColor: "#DC2626",
                            fontSize: "0.8rem",
                            fontWeight: 600,
                            px: 2,
                            py: 0.9,
                            borderRadius: 2.5,
                            textTransform: "none",
                            whiteSpace: "nowrap",
                            "&:hover": { borderColor: "#B91C1C", bgcolor: "#FEF2F2" },
                        }}
                    >
                        Logout All Other Sessions
                    </Button>
                </Box>
            </DialogContent>
        </Dialog>
    );
}